import { Link } from "react-router-dom";
import profile from "@/data/profile";
import { MessageCircle, Instagram, Linkedin, Mail } from "lucide-react";
import { trackOutbound } from "@/analytics/ga4";
import { BrandMark } from "@/components/site/Nav";

const FOOTER_NAV = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/work", label: "Portfolio" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

const BehanceIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
        <path d="M22 7h-7v-2h7v2zm1.726 10c-.442 1.297-2.029 3-5.101 3-3.074 0-5.564-1.729-5.564-5.675 0-3.91 2.325-5.92 5.466-5.92 3.082 0 4.964 1.782 5.375 4.426.078.506.109 1.188.095 2.14h-8.027c.13 3.211 3.483 3.312 4.588 2.029h3.168zm-7.686-4h4.965c-.105-1.547-1.136-2.219-2.477-2.219-1.466 0-2.277.768-2.488 2.219zm-9.574 6.988h-6.466v-15.973h6.953c5.476.081 5.58 5.444 2.72 6.906 3.461 1.26 3.577 8.475-3.207 9.067zm-3.466-9.06h3.13c2.385 0 2.5-3.39 0-3.39h-3.13v3.39zm0 6.072h3.179c2.687 0 2.928-3.604 0-3.604h-3.179v3.604z" />
    </svg>
);

// Each channel carries its own brand-colour gradient.
const SOCIALS = [
    { id: "instagram", href: profile.socials.instagram, label: "Instagram", Icon: Instagram, external: true, grad: "linear-gradient(135deg, #833AB4 0%, #C13584 52%, #5B51D8 100%)", glow: "rgba(193, 53, 132, 0.5)" },
    { id: "behance", href: profile.socials.behance, label: "Behance", Icon: BehanceIcon, external: true, grad: "linear-gradient(135deg, #1769FF 0%, #0A3AB5 100%)", glow: "rgba(23, 105, 255, 0.5)" },
    { id: "linkedin", href: profile.socials.linkedin, label: "LinkedIn", Icon: Linkedin, external: true, grad: "linear-gradient(135deg, #0A66C2 0%, #08477F 100%)", glow: "rgba(10, 102, 194, 0.5)" },
    { id: "whatsapp", href: profile.socials.whatsapp, label: "WhatsApp", Icon: MessageCircle, external: true, grad: "linear-gradient(135deg, #25D366 0%, #0B7A54 100%)", glow: "rgba(37, 211, 102, 0.5)" },
    { id: "email", href: `mailto:${profile.email}`, label: "Email", Icon: Mail, external: false, grad: "linear-gradient(135deg, #EA4335 0%, #B31412 100%)", glow: "rgba(234, 67, 53, 0.5)" },
];

export const Footer = () => {
    const year = new Date().getFullYear();

    return (
        <footer
            data-testid="site-footer"
            className="relative border-t"
            style={{ borderColor: "var(--pg-border)", background: "var(--pg-bg)" }}
        >
            <div className="pg-container py-16">
                <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <Link to="/" className="inline-flex items-center gap-2" aria-label="Pranto.Gfx home" data-testid="footer-logo">
                            <BrandMark />
                        </Link>
                        <p
                            className="mt-5 max-w-sm text-sm leading-relaxed"
                            style={{ color: "var(--pg-fg-muted)" }}
                        >
                            Premium Brand Identity, Logo Design, Creative Direction, UI/UX, And Visual Systems.
                        </p>
                    </div>

                    <div className="md:col-span-3">
                        <h4
                            className="font-mono text-[11px] uppercase tracking-[0.22em]"
                            style={{ color: "var(--pg-fg-muted)" }}
                        >
                            Navigate
                        </h4>
                        <ul className="mt-4 space-y-2.5">
                            {FOOTER_NAV.map((link) => (
                                <li key={link.to}>
                                    <Link
                                        to={link.to}
                                        className="pg-link-underline text-sm"
                                        style={{ color: "var(--pg-fg)" }}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <h4
                            className="font-mono text-[11px] uppercase tracking-[0.22em]"
                            style={{ color: "var(--pg-fg-muted)" }}
                        >
                            Connect
                        </h4>
                        <div className="mt-4 flex flex-wrap gap-3">
                            {SOCIALS.map(({ id, href, label, Icon, external, grad, glow }) => (
                                <a
                                    key={id}
                                    href={href}
                                    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                                    onClick={() => trackOutbound(id)}
                                    aria-label={label}
                                    data-testid={`footer-${id}`}
                                    className="pg-social inline-flex h-11 w-11 items-center justify-center rounded-full"
                                    style={{ background: grad, color: "#FFFFFF", "--brand-glow": glow }}
                                >
                                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                                </a>
                            ))}
                        </div>
                        <a
                            href={`mailto:${profile.email}`}
                            className="mt-5 inline-block pg-link-underline text-sm"
                            style={{ color: "var(--pg-fg)" }}
                        >
                            {profile.email}
                        </a>
                    </div>
                </div>

                <div className="mt-12 pg-divider" />

                <div className="mt-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                    <p
                        className="font-mono text-[11px] uppercase tracking-[0.22em]"
                        style={{ color: "var(--pg-fg-muted)" }}
                    >
                        {"\u00A9"} {year} Pranto.Gfx · Crafted with intent.
                    </p>
                    <p
                        className="font-mono text-[11px] uppercase tracking-[0.22em]"
                        style={{ color: "var(--pg-fg-muted)" }}
                    >
                        Studio led by Pranto · Available for select projects
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
